// 管理会社向けのステータス進行可否判定ユーティリティ
// このユーティリティは管理会社画面からのみ呼ばれることを想定しています。
import type { Deal, DealWithPrices } from '~/models/Deal';
import {
  IN_ESTIMATION,
  NOW_ACCEPTING,
  SEND_ESTIMATE,
  IN_CONSTRUCTION,
  AWAITING_ACCEPTANCE,
  OWN_IN_ESTIMATION,
  OWN_SEND_ESTIMATE,
  OWN_IN_CONSTRUCTION,
  OWN_AWAITING_ACCEPTANCE,
  ownStatusMovingValidation,
  statusMovingValidation,
  OWN_FINISHED
} from '~/models/Status';
import type { ItandiAccount } from '~/models/ItandiAccount';
import type { DealEstimationContext } from '~/models/DealEstimate';
import type { DealEstimationHistoryContext } from '~/models/DealEstimationHistory';
import type {
  DealInspectionReportImage,
  DealCompletionReportImage
} from '~/models/DealReportImage';
import type { DealEstimationHeader } from '~/models/DealEstimationHeader';

export interface StatusForwardDeps {
  dealEstimations: DealEstimationContext | null;
  dealEstimationHistories: DealEstimationHistoryContext | null;
  inspectionImages: DealInspectionReportImage[] | null;
  completionImages: DealCompletionReportImage[] | null;
  estimationHeaders: DealEstimationHeader[] | null | undefined;
}

export const forwardStatusValidation = (
  deal: Deal | DealWithPrices,
  account: ItandiAccount | null,
  deps: StatusForwardDeps
): boolean => {
  const {
    dealEstimations,
    dealEstimationHistories,
    inspectionImages,
    completionImages,
    estimationHeaders
  } = deps;

  if (!account) return false;
  if (deal.dealVendorCompanies.length === 0) return false;

  switch (deal.statusId) {
    case OWN_IN_ESTIMATION:
    case OWN_SEND_ESTIMATE:
    case OWN_IN_CONSTRUCTION:
    case OWN_AWAITING_ACCEPTANCE:
      return ownStatusMovingValidation[deal.statusId](account);
    case OWN_FINISHED:
      return false;

    case NOW_ACCEPTING:
      return statusMovingValidation[NOW_ACCEPTING](account);

    case IN_ESTIMATION: {
      if (!dealEstimations || !dealEstimationHistories || !inspectionImages)
        return false;
      return statusMovingValidation[IN_ESTIMATION](
        deal,
        dealEstimations,
        dealEstimationHistories,
        inspectionImages,
        account.isAdmin,
        true
      );
    }
    case SEND_ESTIMATE: {
      if (!estimationHeaders || !dealEstimations) return false;
      return statusMovingValidation[SEND_ESTIMATE](
        estimationHeaders,
        dealEstimations,
        true
      );
    }
    case IN_CONSTRUCTION: {
      if (
        !dealEstimations ||
        !dealEstimationHistories ||
        !inspectionImages ||
        !completionImages ||
        !estimationHeaders
      )
        return false;
      const requestHeader =
        estimationHeaders.find((h) => h.status === 'request') ?? null;
      return statusMovingValidation[IN_CONSTRUCTION](
        deal,
        dealEstimations,
        dealEstimationHistories,
        requestHeader,
        inspectionImages,
        completionImages,
        false
      );
    }

    case AWAITING_ACCEPTANCE:
      return statusMovingValidation[AWAITING_ACCEPTANCE](account);

    default:
      return false;
  }
};
